import type {
  PrinterPageSize,
  PrinterRollWidth,
  PrinterType,
} from '@rematter/frontend-lib-api-client';
import { enumPrinterType } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { PrinterDetailRecord } from '../types/sidecar';

export type PrinterFormState = {
  name: string;
  printerType: PrinterType;
  pageSize: PrinterPageSize | null;
  rollWidth: PrinterRollWidth | null;
};

const initialState = (): PrinterFormState => ({
  name: '',
  printerType: enumPrinterType.Standard,
  pageSize: null,
  rollWidth: null,
});

const mapPrinterToForm = (printer: PrinterDetailRecord): PrinterFormState => ({
  name: printer.name,
  printerType: printer.printerType,
  pageSize: printer.pageSize,
  rollWidth: printer.rollWidth,
});

export const $printerForm = atom<PrinterFormState>(initialState());
export const $discardPrinterBaseline = atom<PrinterFormState | null>(null);
export const $lastPopulatedPrinterId = atom<string | undefined>(undefined);
export const $isPrinterFormPopulated = atom<boolean>(false);

export const $hasUnsavedPrinterChanges = atom((get) => {
  const form = get($printerForm);
  const baseline = get($discardPrinterBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitPrinterForm = atom((get) => {
  const form = get($printerForm);
  if (!form.name.trim()) return false;
  return true;
});

export const $populatePrinterFormFromPrinter = atom(
  null,
  (_get, set, printer: PrinterDetailRecord) => {
    const formState = mapPrinterToForm(printer);
    set($printerForm, formState);
    set($discardPrinterBaseline, formState);
    set($lastPopulatedPrinterId, printer.id);
    set($isPrinterFormPopulated, true);
  },
);

export const $resetPrinterFormToPrinter = atom(null, (get, set) => {
  const baseline = get($discardPrinterBaseline);
  if (baseline) {
    set($printerForm, baseline);
  }
});

export const $populatePrinterFormAction = atom(
  null,
  (get, set, printer: PrinterDetailRecord | null | undefined) => {
    if (!printer) return;

    const currentPrinterId = printer.id;
    const lastPrinterId = get($lastPopulatedPrinterId);

    if (currentPrinterId !== lastPrinterId) {
      set($isPrinterFormPopulated, false);
    }

    if (!get($isPrinterFormPopulated)) {
      set($populatePrinterFormFromPrinter, printer);
    }
  },
);

export const $resetPrinterForm = atom(null, (_get, set) => {
  set($printerForm, initialState());
  set($discardPrinterBaseline, null);
  set($lastPopulatedPrinterId, undefined);
  set($isPrinterFormPopulated, false);
});
